import { createFileRoute } from '@tanstack/react-router';
import { Bot, Zap, MessageSquare, Wrench, Users, BarChart3, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/agents-platform')({
  component: AgentsPlatformPage,
});

const features = [
  {
    iconName: "Bot",
    title: "Visual Agent Builder",
    description: "Design agents with a drag-and-drop canvas. Define goals, tools, and behaviors visually.",
  },
  {
    iconName: "Wrench",
    title: "Tool Integration",
    description: "Give agents access to 2000+ tools and APIs. Agents decide when and how to use them.",
  },
  {
    iconName: "MessageSquare",
    title: "Conversation Memory",
    description: "Agents remember context across sessions. Long-term and short-term memory management.",
  },
  {
    iconName: "Users",
    title: "Multi-Agent Crews",
    description: "Orchestrate multiple specialized agents working together on complex tasks.",
  },
  {
    iconName: "Zap",
    title: "Real-Time Execution",
    description: "Stream agent responses and actions. See agents think and act in real-time.",
  },
  {
    iconName: "BarChart3",
    title: "Analytics & Monitoring",
    description: "Track agent performance, token usage, and success rates with detailed metrics.",
  },
];

const agentTypes = [
  {
    type: "Chat Agents",
    description: "Conversational agents for customer support, sales, or internal help desks.",
    capabilities: ["Natural conversation", "Context awareness", "Escalation handling"],
  },
  {
    type: "Task Agents",
    description: "Goal-oriented agents that complete specific tasks autonomously.",
    capabilities: ["Multi-step reasoning", "Tool usage", "Error recovery"],
  },
  {
    type: "Research Agents",
    description: "Agents that gather, analyze, and synthesize information from multiple sources.",
    capabilities: ["Web search", "Document analysis", "Report generation"],
  },
  {
    type: "Data Agents",
    description: "Agents that work with structured data, databases, and analytics.",
    capabilities: ["SQL generation", "Data visualization", "Insight extraction"],
  },
];

const capabilities = [
  "Natural language agent creation",
  "Pre-built agent templates",
  "Custom tool definition",
  "Memory and context management",
  "Multi-agent orchestration",
  "Streaming responses",
  "Execution monitoring",
  "Version control",
];

const faqItems = [
  {
    question: "What LLMs power the agents?",
    answer: "Agents can use any LLM available through our AI Gateway—OpenAI GPT-4, Anthropic Claude, Google Gemini, and more. You can even switch models per agent based on the task requirements.",
  },
  {
    question: "How do agents access external tools?",
    answer: "Agents connect to any of our 2000+ integrations or custom APIs you define. The agent decides when to use tools based on its goals. All tool usage is logged and can be restricted by policies.",
  },
  {
    question: "Can I deploy agents for customer-facing use?",
    answer: "Yes, agents can be deployed as chat widgets, API endpoints, or integrated into your applications. Rate limiting, authentication, and moderation features ensure safe production deployment.",
  },
  {
    question: "How do multi-agent crews work?",
    answer: "Define specialized agents and orchestrate them with a supervisor agent or workflow. For example, a research agent gathers data, an analysis agent processes it, and a writing agent creates reports.",
  },
];

function AgentsPlatformPage() {
  return (
    <div>
      <ContentHeader
        title="AI Agents Platform"
        description="Build autonomous AI agents that can reason, use tools, and accomplish complex tasks. From simple chatbots to sophisticated multi-agent systems."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "Agents Platform" },
        ]}
        badge="Feature"
      />

      {/* Features */}
      <ContentSection
        id="features"
        title="Platform Features"
        background="muted"
      >
        <FeatureShowcase features={features} columns={3} variant="cards" />
      </ContentSection>

      {/* Agent Types */}
      <ContentSection
        id="agent-types"
        title="Agent Types"
        description="Build different types of agents for different use cases."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {agentTypes.map((agent) => (
            <div key={agent.type} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg">{agent.type}</h3>
              <p className="text-sm text-muted-foreground mt-2">{agent.description}</p>
              <ul className="mt-4 space-y-2">
                {agent.capabilities.map((cap) => (
                  <li key={cap} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    {cap}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Building Agents */}
      <ContentSection
        id="building"
        title="Three Ways to Build"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 mb-4">
              <MessageSquare className="h-6 w-6 text-blue-500" />
            </div>
            <h3 className="font-semibold">Natural Language</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Describe your agent in plain English. AI generates the configuration automatically.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/10 mb-4">
              <Bot className="h-6 w-6 text-purple-500" />
            </div>
            <h3 className="font-semibold">Visual Canvas</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Drag and drop nodes to design agent behavior, tools, and decision logic visually.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10 mb-4">
              <Wrench className="h-6 w-6 text-green-500" />
            </div>
            <h3 className="font-semibold">Templates</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Start from pre-built templates for common use cases and customize as needed.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Capabilities */}
      <ContentSection id="capabilities" title="Full Capabilities">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((capability) => (
            <div key={capability} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{capability}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Build Your First Agent"
        description="Create powerful AI agents in minutes. Start with a template or build from scratch."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Templates", href: "/features" }}
        background="primary"
      />
    </div>
  );
}
